import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { StateMessage } from "../components/QueryStates.tsx";
import { BoxWhiskerChart } from "../components/BoxWhiskerChart.tsx";
import { QuoteFreshnessChip } from "../components/QuoteFreshnessChip.tsx";
import { QuoteQualityCard } from "../components/QuoteQualityCard.tsx";
import { SpreadCaptureCard } from "../components/SpreadCaptureCard.tsx";
import { AdverseSelectionCard } from "../components/AdverseSelectionCard.tsx";
import { LpMarkoutCard } from "../components/LpMarkoutCard.tsx";
import { ExecCostCard } from "../components/ExecCostCard.tsx";
import { isDexVenue } from "../lib/venues.ts";
import { useSpreadBuckets, type SpreadMetric } from "../hooks/useSpreadBuckets.ts";
import { toBoxSeries, allUnusable } from "../lib/boxwhisker.ts";
import type { Binding } from "../bindings.ts";

// Window (span shown) and resolution (bucket interval) are independent. Coarser
// intervals pool more observations per bucket -- useful for thin markets.
const WINDOWS = [
  { label: "15m", minutes: 15 },
  { label: "1h", minutes: 60 },
  { label: "4h", minutes: 240 },
  { label: "1d", minutes: 1440 },
];
const INTERVALS = [
  { label: "30s", seconds: 30 },
  { label: "1m", seconds: 60 },
  { label: "5m", seconds: 300 },
  { label: "10m", seconds: 600 },
  { label: "15m", seconds: 900 },
  { label: "30m", seconds: 1800 },
  { label: "60m", seconds: 3600 },
];

function fmtWindow(m: number): string {
  if (m >= 1440) return m / 1440 + "d";
  if (m >= 60) return m / 60 + "h";
  return m + "m";
}

const seg = (active: boolean): CSSProperties => ({
  padding: "3px 9px",
  fontSize: 12,
  lineHeight: 1.4,
  cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)",
  background: active ? "rgba(102,153,255,0.18)" : "transparent",
  color: active ? "#cfe0ff" : "#8a93a6",
  borderRadius: 5,
});

interface MetricSpec {
  metric: SpreadMetric;
  title: string;
  subtitle?: string; // optional muted disambiguation line under the title
  captionNoun: string; // "Fresh-mid activity" | "Quote coverage"
  covNoun: string; // tooltip: "fresh / total trades" | "two-sided / total quotes"
  yMin?: number; // 0 for effective/quoted; undefined for realized (negative ok)
  zeroLine?: boolean;
}

const METRICS: MetricSpec[] = [
  { metric: "effective", title: "Effective spread", subtitle: "full quoted-spread equivalent (unsigned \u00d72) \u2014 distinct from the signed half-spread in Adverse selection", captionNoun: "Fresh-mid activity", covNoun: "fresh / total trades", yMin: 0 },
  { metric: "realized", title: "Realized spread (MM-signed)", captionNoun: "Fresh-mid activity", covNoun: "fresh / total trades", yMin: undefined, zeroLine: true },
  { metric: "quoted", title: "Quoted spread (book)", captionNoun: "Quote coverage", covNoun: "two-sided / total quotes", yMin: 0 },
];

function MetricBox({
  binding,
  spec,
  windowMinutes,
  bucketSeconds,
}: {
  binding: Binding;
  spec: MetricSpec;
  windowMinutes: number;
  bucketSeconds: number;
}) {
  const q = useSpreadBuckets(spec.metric, {
    clientId: binding.clientId,
    venue: binding.venue,
    pair: binding.pair,
    lookbackMinutes: windowMinutes,
    bucketSeconds,
  });

  const series = useMemo(
    () => toBoxSeries(q.rows, { lookbackMinutes: windowMinutes, bucketSeconds, nowMs: Date.now() }),
    [q.rows, windowMinutes, bucketSeconds],
  );

  let body: ReactNode;
  if (q.isPending) body = <StateMessage kind="loading">Loading…</StateMessage>;
  else if (q.isError) body = <StateMessage kind="error">Could not load. Retrying…</StateMessage>;
  else if (q.count === 0)
    body = <StateMessage kind="empty">No data in the last {fmtWindow(windowMinutes)}.</StateMessage>;
  else if (allUnusable(series))
    body = (
      <StateMessage kind="info">
        Observations exist in the window, but none were usable (no fresh mid / no two-sided
        quote). Showing no fabricated values.
      </StateMessage>
    );
  else
    body = (
      <BoxWhiskerChart
        series={series}
        bucketSeconds={bucketSeconds}
        covNoun={spec.covNoun}
        yMin={spec.yMin}
        zeroLine={spec.zeroLine}
        height={190}
      />
    );

  return (
    <div style={{ marginTop: 8 }}>
      <div className="panel-head" style={{ marginBottom: 8 }}>
        <h3 style={{ fontSize: 14 }}>{spec.title}</h3>
        {spec.subtitle && (
          <div style={{ fontSize: 11, color: "#8a93a6", marginTop: 2, marginBottom: 2 }}>
            {spec.subtitle}
          </div>
        )}
        <div className="panel-meta">
          <span className={series.populatedBuckets === 0 ? "warn" : ""}>
            {spec.captionNoun}: {series.populatedBuckets} of {series.expectedBuckets} buckets
          </span>
        </div>
      </div>
      {body}
    </div>
  );
}

// On DEX the 'effective' chart is reframed to taker execution cost vs the
// external reference (pool mid is endogenous on a CLMM); retitle + reroute.
function dexMetricSpec(venue: string, spec: MetricSpec): MetricSpec {
  if (isDexVenue(venue) && spec.metric === "effective") {
    return { ...spec, metric: "exec_cost", title: "Execution cost vs reference", yMin: undefined, zeroLine: true };
  }
  return spec;
}


export function EffectiveSpreadPanel({ binding }: { binding: Binding }) {
  const [windowMinutes, setWindowMinutes] = useState(240);
  const [bucketSeconds, setBucketSeconds] = useState(300);

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>{binding.label}</h3>
        <div className="panel-meta" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <QuoteFreshnessChip
            venue={binding.venue}
            pair={binding.pair}
            clientId={binding.clientId}
          />
          <span>window × resolution are independent</span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
          margin: "4px 0 12px",
        }}
      >
        {!isDexVenue(binding.venue) && (
          <QuoteQualityCard
            venue={binding.venue}
            pair={binding.pair}
            clientId={binding.clientId}
          />
        )}
        {!isDexVenue(binding.venue) && (
          <SpreadCaptureCard
            venue={binding.venue}
            pair={binding.pair}
            clientId={binding.clientId}
          />
        )}
        {isDexVenue(binding.venue) ? (
          <>
            <ExecCostCard
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
            <LpMarkoutCard
              venue={binding.venue}
              pair={binding.pair}
              clientId={binding.clientId}
            />
          </>
        ) : (
          <AdverseSelectionCard
            venue={binding.venue}
            pair={binding.pair}
            clientId={binding.clientId}
          />
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, margin: "4px 0 24px" }}>
        <div style={{ display: "inline-flex", gap: 4 }} role="group" aria-label="Window">
          {WINDOWS.map((w) => (
            <button key={w.minutes} style={seg(w.minutes === windowMinutes)} onClick={() => setWindowMinutes(w.minutes)}>
              {w.label}
            </button>
          ))}
        </div>
        <div style={{ display: "inline-flex", gap: 4 }} role="group" aria-label="Interval">
          {INTERVALS.map((iv) => (
            <button key={iv.seconds} style={seg(iv.seconds === bucketSeconds)} onClick={() => setBucketSeconds(iv.seconds)}>
              {iv.label}
            </button>
          ))}
        </div>
      </div>

      {METRICS
        .filter((spec) =>
          isDexVenue(binding.venue)
            ? spec.metric !== "quoted" && spec.metric !== "realized"
            : true,
        )
        .map((spec) => dexMetricSpec(binding.venue, spec))
        .map((spec) => (
        <MetricBox
          key={spec.metric}
          binding={binding}
          spec={spec}
          windowMinutes={windowMinutes}
          bucketSeconds={bucketSeconds}
        />
      ))}
    </div>
  );
}
